import { useRuntimeConfig, useRequestURL, useRoute } from 'nuxt/app';

function makeBasicToken(user, pass) {
  const raw = `${user}:${pass}`;
  if (process.server) {
    return Buffer.from(raw).toString('base64');
  }
  return btoa(raw);
}

const encodeReadableParam = (str) =>
  encodeURIComponent(str).replace(/%2F/g, '/').replace(/%3A/g, ':');

export const useRequest = (url, options = {}) => {
  const requestUrl = useRequestURL();
  const route = useRoute();

  const urlObj = new URL(requestUrl.href);
  const currentClientUrl = (urlObj.origin + urlObj.pathname).replace(
    /\/+$/,
    '',
  );

  const langPrefix = route.params.lang === 'en' ? '/en' : '';
  const computedBaseURL = `${langPrefix}/api`;

  const instance = $fetch.create({
    baseURL: computedBaseURL,
    onRequest({ options }) {
      if (process.server) {
        const { apiBasicAuth } = useRuntimeConfig();
        if (apiBasicAuth?.user) {
          const token = makeBasicToken(apiBasicAuth.user, apiBasicAuth.pass || '');
          options.headers = {
            ...(options.headers || {}),
            Authorization: `Basic ${token}`,
          };
        }
      }
    },
    onResponseError({ request, options: reqOptions, response }) {
      const context = {
        url: String(request),
        method: reqOptions?.method || 'GET',
        status: response.status,
        statusText: response.statusText,
        requestParams: reqOptions?.params,
        responseBody: response._data,
      };
      console.error('[useRequest] Request failed:', context);
    },
  });

  const restParams = {
    ...(options.params || {}),
    ...(options.query || {}),
  };
  delete options.query;
  delete options.params;

  const method = (options.method || 'GET').toUpperCase();
  if (method === 'GET') {
    options.retry = options.retry ?? (process.server ? 5 : 8);

    options.retryStatusCodes = options.retryStatusCodes || [ 400, 408, 409, 425, 429];

    options.retryDelay =
      options.retryDelay ??
      ((context) => {
        const retryAfter = Number(
          context.response?.headers?.get('Retry-After'),
        );
        if (Number.isFinite(retryAfter)) return retryAfter * 1000;

        const totalRetries = options.retry;
        const remaining = context.options?.retry ?? 0;
        const attempt = totalRetries - remaining;

        const maxDelay = process.server ? 3000 : 10000;
        const base = Math.min(300 * 2 ** attempt, maxDelay);
        const jitter = Math.random() * 0.3 * base;
        return base + jitter;
      });
  }

  const clean = (s) => String(s || '').replace(/^\/+|\/+$/g, '');
  const [rawPath, existingQuery] = clean(url).split('?');
  const basePath = `/${rawPath}`;

  const restQueryString = new URLSearchParams(restParams).toString();
  const urlParamString = `url=${encodeReadableParam(currentClientUrl)}`;
  const queryString = [existingQuery, urlParamString, restQueryString]
    .filter(Boolean)
    .join('&');

  const fullPath = `${basePath}?${queryString}`;

  return instance(fullPath, options);
};